import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import useHandleRedirect from '../../Hooks/useHandleRedirect';
import Layout from '../../Utils/Navbar/navbar-layout-component';
import Footer from '../../Utils/Footer/footer-component';
import MediaComponent from '../../Utils/Media/media-component';
import { get, downloadFile } from '../../Services/ApiService';
import { ServerEndpoint } from '../../Redux/endpoints.ts';
import * as authActions from '../../Redux/Auth/AuthActions';
import { getAccountData } from '../../Public/account-data.js';
import { getAdminOrderDetailStrings } from '../../Public/admin-order-detail-data.js';
import SystemDataService from '../../Services/SystemDataService';
import { ButtonPrimary, ButtonSecondary } from '../../Utils/Buttons/button-component';
import { setToast } from '../../Redux/Toast/ToastActions';

const STATUS_CONFIG = {
    paid:               { badge: 'bg-white text-secondary-500 border-secondary-300',  dot: 'bg-secondary-400', bar: 'bg-secondary-400' },
    received:           { badge: 'bg-teal-50 text-teal-600 border-teal-200',          dot: 'bg-teal-400',      bar: 'bg-teal-400' },
    processing:         { badge: 'bg-yellow-50 text-yellow-700 border-yellow-200',    dot: 'bg-yellow-400',    bar: 'bg-yellow-400' },
    ready_for_shipping: { badge: 'bg-indigo-50 text-indigo-600 border-indigo-200',    dot: 'bg-indigo-400',    bar: 'bg-indigo-400' },
    ready_for_pickup:   { badge: 'bg-purple-50 text-purple-600 border-purple-200',    dot: 'bg-purple-400',    bar: 'bg-purple-400' },
    shipped:            { badge: 'bg-blue-50 text-blue-600 border-blue-200',          dot: 'bg-blue-400',      bar: 'bg-blue-400' },
    picked_up:          { badge: 'bg-emerald-50 text-emerald-600 border-emerald-200', dot: 'bg-emerald-400',   bar: 'bg-emerald-400' },
    delivered:          { badge: 'bg-green-50 text-green-600 border-green-200',       dot: 'bg-green-400',     bar: 'bg-green-400' },
    done:               { badge: 'bg-green-100 text-green-700 border-green-300',      dot: 'bg-green-500',     bar: 'bg-green-500' },
    payment_failed:     { badge: 'bg-red-50 text-red-600 border-red-200',             dot: 'bg-red-400',       bar: 'bg-red-400' },
};

// ─── Order Card ───────────────────────────────────────────────────────────────

const OrderCard = ({ order, t, statusLabels, links, onDownloadInvoice }) => {
    const cfg   = STATUS_CONFIG[order.status] || STATUS_CONFIG.paid;
    const label = statusLabels[order.status] || order.status;
    const date  = new Date(order.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const [downloading, setDownloading] = useState(false);

    const handleInvoiceClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDownloading(true);
        try {
            await onDownloadInvoice(order.id);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Link
            to={`/success?orderId=${order.id}`}
            className="group bg-white border border-gray-100 rounded-[2.5rem] overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-xl hover:shadow-primary-950/10 hover:-translate-y-0.5 block"
        >
            {/* Dark header */}
            <div className="bg-primary-950 px-6 py-5 flex items-center justify-between">
                <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border ${cfg.badge}`}>
                    <span className={`w-2 h-2 rounded-full shrink-0 ${cfg.dot}`} />
                    {label}
                </span>
                <span className="text-xs text-white/40">{date}</span>
            </div>

            <div className="p-7 flex flex-col gap-5">
                {/* Item thumbnails */}
                <div className="flex -space-x-2">
                    {(order.items || []).slice(0, 5).map((item, i) => (
                        <div key={i} className="w-11 h-11 rounded-xl bg-gray-50 border-2 border-white shadow-sm overflow-hidden p-1.5 flex items-center justify-center">
                            <MediaComponent media={item.image} className="w-full h-full object-contain mix-blend-multiply" useSekeleton={false} />
                        </div>
                    ))}
                    {(order.items || []).length > 5 && (
                        <div className="w-11 h-11 rounded-xl bg-primary-950 border-2 border-white flex items-center justify-center text-[10px] font-semibold text-white">
                            +{order.items.length - 5}
                        </div>
                    )}
                </div>

                {/* Items + total */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-50">
                    <span className="text-sm text-gray-500">{order.items.length} {order.items.length === 1 ? 'item' : 'items'}</span>
                    <span className="text-2xl font-black text-primary-950 italic er">
                        ${order.total?.toFixed(2)}
                    </span>
                </div>

                {/* Download invoice */}
                <button
                    onClick={handleInvoiceClick}
                    disabled={downloading}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-2xl border border-gray-100 text-xs font-bold text-gray-500 hover:border-primary-950 hover:text-primary-950 transition-all disabled:opacity-40"
                >
                    {downloading ? (
                        <span className="w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                    ) : (
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                    )}
                    {downloading ? 'Fetching invoice...' : 'Download Invoice'}
                </button>
            </div>
        </Link>
    );
};

// ─── Main Account Screen ─────────────────────────────────────────────────────
const AccountScreen = () => {
    const dispatch = useDispatch();
    const navigate = useHandleRedirect();
    const { user } = useSelector((state) => state.auth);
    const { language = 'en', links = [] } = useSelector((state) => state.systemData || {});
    const t = useMemo(() => getAccountData(language), [language]);
    const statusLabels = useMemo(() => getAdminOrderDetailStrings(language).status.labels, [language]);

    const [orders, setOrders] = useState([]);
    const [ordersLoading, setOrdersLoading] = useState(true);

    const handleDownloadInvoice = useCallback(async (orderId) => {
        try {
            await downloadFile(`${ServerEndpoint.USER_ORDER_INVOICE_PDF}/${orderId}/invoice/pdf`, `invoice-${orderId}.pdf`);
        } catch (err) {
            const status = err.response?.status;
            if (status === 401 || status === 403) dispatch(setToast('error', 'Access denied'));
            else if (status === 404)              dispatch(setToast('error', 'Invoice not available yet — check back once payment is confirmed'));
            else                                  dispatch(setToast('error', 'Failed to download invoice. Please try again.'));
        }
    }, [dispatch]);

    const logo = SystemDataService.byLabel(links, 'Logo Full') || SystemDataService.byLabel(links, 'Logo');

    useEffect(() => {
        window.scrollTo(0, 0);
        get(ServerEndpoint.USER_ORDERS, true)
            .then(res => { if (res?.data?.status) setOrders(res.data.data || []); })
            .catch(() => {})
            .finally(() => setOrdersLoading(false));
    }, []);

    const totalSpent = orders.reduce((sum, o) => sum + (o.total || 0), 0);
    const displayName = user?.name || user?.email?.split('@')[0] || '';

    return (
        <div className="min-h-screen bg-gray-50/50 font-primary selection:bg-secondary-100">
            <Layout>

                {/* ── PROFILE HERO ── */}
                <div className="bg-primary-950 w-full relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-secondary-500/10 rounded-full blur-[120px] -mr-48 -mt-48" />

                    <div className="max-w-7xl mx-auto px-8 py-20 lg:py-28 relative z-10">
                        <div className="flex flex-col lg:flex-row items-center lg:items-end gap-12 text-center lg:text-left">

                            {/* Avatar */}
                            <div className="relative">
                                <div className="w-32 h-32 rounded-[2.5rem] bg-secondary-500 flex items-center justify-center shadow-2xl">
                                    <span className="text-white text-5xl font-black italic er uppercase">
                                        {displayName[0]}
                                    </span>
                                </div>
                                <div className="absolute -bottom-2 -right-2 bg-white p-2 rounded-xl shadow-lg">
                                    <MediaComponent media={logo?.image} useSekeleton={false} className="w-6 h-6 object-contain" />
                                </div>
                            </div>

                            <div className="flex-grow">
                                <h1 className="text-6xl lg:text-8xl text-white font-black uppercase italic er leading-none">
                                    {displayName}
                                </h1>
                                <p className="text-lg font-medium text-white/40 mt-4 tracking-wide">
                                    {user?.email}
                                </p>
                            </div>

                            <div className="flex flex-col gap-3 min-w-[240px]">
                                <ButtonPrimary
                                    onClick={() => navigate('/shop')}
                                    className="!w-full !rounded-2xl !py-5 text-[10px] uppercase font-black tracking-widest shadow-xl shadow-secondary-500/20"
                                >
                                    Shop
                                </ButtonPrimary>
                                <ButtonSecondary
                                    onClick={() => dispatch(authActions.logout())}
                                    className="!w-full !rounded-2xl !py-5 text-[10px] uppercase font-black tracking-widest !bg-transparent !border-white/10 !text-white/30 hover:!text-white hover:!border-white/40"
                                >
                                    Sign Out
                                </ButtonSecondary>
                            </div>
                        </div>

                        {/* Stats */}
                        <div className="flex flex-col md:grid md:grid-cols-3 gap-8 mt-20 pt-12 border-t border-white/5">
                            {[
                                { label: 'Total Orders',    val: orders.length,            color: 'text-white' },
                                { label: 'Total Spent',     val: `$${totalSpent.toFixed(2)}`, color: 'text-secondary-500' },
                                { label: 'Account Status',  val: 'BH-PRO',                 color: 'text-white' },
                            ].map((stat, i) => (
                                <div key={i} className="bg-white/5 p-8 rounded-[2rem] border border-white/5">
                                    <p className="text-[9px] font-black uppercase  text-white/30 mb-4">{stat.label}</p>
                                    <p className={`text-5xl font-black italic er ${stat.color}`}>{ordersLoading && i < 2 ? '...' : stat.val}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ── ORDERS ── */}
                <div className="max-w-7xl mx-auto px-8 py-24">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
                        <div>
                            <h2 className="text-5xl lg:text-6xl text-primary-950 font-black uppercase italic er leading-none">
                                My Orders
                            </h2>
                        </div>
                        <div className="h-px flex-1 bg-gray-100 hidden md:block mx-10 mb-4" />
                    </div>

                    {ordersLoading ? (
                        <div className="flex flex-col items-center justify-center py-32 opacity-20">
                            <div className="w-12 h-12 border-4 border-primary-950 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : orders.length === 0 ? (
                        <div className="bg-white rounded-[3rem] p-20 text-center border border-gray-100 shadow-xl shadow-gray-200/20">
                            <p className="text-8xl text-gray-50 font-black uppercase italic er mb-8">No Orders</p>
                            <ButtonPrimary onClick={() => navigate('/shop')} className="!px-12 !rounded-full">
                                Start Shopping
                            </ButtonPrimary>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {[...orders].reverse().filter(o => o.status !== 'payment_failed').map(order => (
                                <OrderCard key={order.id} order={order} t={t} statusLabels={statusLabels} links={links} onDownloadInvoice={handleDownloadInvoice} />
                            ))}
                        </div>
                    )}
                </div>

            </Layout>
            <Footer />
        </div>
    );
};

export default AccountScreen;
